import { Link } from "react-router-dom";

const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: "20px",
  color: "var(--color-ink)",
  margin: 0,
};

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };
const listStyle = { display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" };
const tableCellStyle = { padding: "10px 12px", fontSize: "14px", color: "rgba(10,9,7,0.85)", borderBottom: "1px solid rgba(10,9,7,0.1)" };
const tableHeadCellStyle = { ...tableCellStyle, fontFamily: "var(--font-serif)", fontSize: "16px", color: "var(--color-ink)", textAlign: "left" };

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      {children}
    </div>
  );
}

/** Sponsoring tiers — amounts and benefits are proposals, not yet fixed
 * prices/terms; flagged for the association to confirm before publishing,
 * same treatment as the membership fee on the Förderer page. */
const TIERS = {
  de: {
    columns: ["Gefährte · ab 250 €/Jahr", "Wegbereiter · ab 750 €/Jahr", "Hauptsponsor · ab 2.000 €/Jahr"],
    rows: [
      ["Logo auf Website (Sponsoren-Sektion)", "✓", "✓", "✓ prominent"],
      ["Nennung im Newsletter", "1×", "Newsletter vom Typ „Allgemeine Informationen“", "Alle Newsletter-Typen"],
      ["Logo auf Event-Material (Banner, Programm)", "–", "✓", "✓ groß"],
      ["Social-Media-Post", "–", "1×/Jahr", "Dankes-Posts zu den Events"],
      ["Transparenzbericht mit Mittelverwendung", "✓", "✓", "✓"],
    ],
    note: "Beträge und Leistungen sind Vorschläge — wir legen sie gemeinsam mit euch final fest.",
  },
  en: {
    columns: ["Companion · from 250 €/yr", "Pathfinder · from 750 €/yr", "Main sponsor · from 2,000 €/yr"],
    rows: [
      ["Logo on website (sponsors section)", "✓", "✓", "✓ prominent"],
      ["Mention in the newsletter", "1×", "\"General information\" newsletter only", "All newsletter types"],
      ["Logo on event materials (banners, programme)", "–", "✓", "✓ large"],
      ["Social media post", "–", "1×/year", "Thank-you posts for our events"],
      ["Transparency report on fund usage", "✓", "✓", "✓"],
    ],
    note: "Amounts and benefits are proposals — we finalise them together with you.",
  },
};

function TierTable({ lang }) {
  const { columns, rows, note } = TIERS[lang];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "560px" }}>
          <thead>
            <tr>
              <th style={tableHeadCellStyle}>
                <span className="sr-only">{lang === "de" ? "Leistung" : "Benefit"}</span>
              </th>
              {columns.map((col) => (
                <th key={col} style={tableHeadCellStyle}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, ...cells]) => (
              <tr key={label}>
                <td style={{ ...tableCellStyle, fontWeight: 600 }}>{label}</td>
                {cells.map((cell, i) => (
                  <td key={i} style={{ ...tableCellStyle, textAlign: "center" }}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: "13px", color: "rgba(10,9,7,0.7)", margin: 0 }}>{note}</p>
    </div>
  );
}

/**
 * Sponsoring pitch copy, as provided directly by the association. Two
 * factual corrections made against our own already-published facts:
 * - "keine Anteilseigner" (no shareholders) is not accurate — the
 *   Gesellschaftsvertrag page lists BrainEntertainments Holding UG as the
 *   one Gesellschafter. Kept the true, intended point instead (§9 of the
 *   Gesellschaftsvertrag: Gesellschafter receive no profit share).
 * - "[deine Telefonnummer]" placeholder replaced with the real number
 *   already published on Impressum/Datenschutz.
 * - "Tales & Legends" (with spaces) normalized to "Tales&Legends" per the
 *   site-wide brand-name convention.
 * German is the primary/authoritative wording (source text was German);
 * English is a courtesy translation.
 */
export function SponsoringDe() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <p style={bodyStyle}>
        Tales&amp;Legends ist jung: ein Tavernenabend mit 20 Gästen, eine Spendenkampagne mit 29 Unterstützern, ein
        Team, das an unserem ersten Wochenende in unserer eigenen Spielwelt Realms of Lorell arbeitet. Wer
        jetzt einsteigt, ist nicht ein Logo unter vielen, sondern Gründungspartner einer Community, die wächst –
        und die ihr Geld nachweislich in Kultur und Jugendarbeit steckt.
      </p>
      <p style={bodyStyle}>
        Tales&amp;Legends erschafft Fantasy-Liverollenspiel-Events, bei denen Geschichten nicht gelesen, sondern
        gelebt werden. Unsere Teilnehmenden sind keine passiven Zuschauer – sie sind Protagonist:innen. Und du
        kannst Teil dieser Geschichte werden.
      </p>

      <Section title="Warum Tales&Legends?">
        <p style={bodyStyle}>
          Tales&amp;Legends ist als gemeinnützige Unternehmergesellschaft (gUG) organisiert – denn für uns ist LARP
          mehr als ein Geschäftsmodell: Es ist Kulturarbeit. Wir glauben daran, dass kreatives Rollenspiel,
          gemeinschaftliches Erzählen und das Leben in anderen Welten einen echten gesellschaftlichen Wert haben –
          für Fantasie, Empathie und Gemeinschaft.
        </p>
        <p style={bodyStyle}>
          Das bedeutet für dich als Sponsor: Deine Unterstützung fließt direkt in die Qualität unserer Events und in
          den Aufbau einer lebendigen Community. Keine Gewinnausschüttung an Gesellschafter — ein Team, das sein
          Herzblut in jede Veranstaltung steckt.
        </p>
        <p style={bodyStyle}>
          Da Sponsoring eine unternehmerische Gegenleistung darstellt, kannst du deine Aufwendungen in der Regel als
          Betriebsausgabe geltend machen. Im Gegenzug erhältst du von uns eine transparente Dokumentation der
          vereinbarten Leistungen. Alternativ kannst du auch eine Spende leisten und dafür eine Spendenbescheinigung
          erhalten — schau dafür auf unserer <Link to="/de/spenden">Spendenseite</Link> vorbei oder kontaktiere uns
          direkt.
        </p>
      </Section>

      <Section title="Was uns darüber hinaus auszeichnet">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Eigene Fantasy-Welt</strong> – unsere Events spielen in einem durchdachten, eigenen Setting mit
            Geschichte, Fraktionen und lebendiger Lore.
          </li>
          <li style={bodyStyle}>
            <strong>Aktive Community</strong> – unsere Teilnehmenden kommen immer wieder, empfehlen weiter und leben
            LARP das ganze Jahr.
          </li>
          <li style={bodyStyle}>
            <strong>Echte Wirkung</strong> – du förderst nicht nur ein Event, sondern eine kulturelle Bewegung.
          </li>
        </ul>
      </Section>

      <Section title="Was wir dir bieten">
        <p style={bodyStyle}>
          Je nach Partnerschaft stehen dir verschiedene Wege offen, präsent zu sein – von der Nennung als
          offizieller Förderer über einen eigenen Stand auf dem Event-Marktplatz bis hin zu weiteren individuellen
          Möglichkeiten. Ob dein Logo auf dem Programm erscheint, dein Produkt bei uns verkauft wird oder dein Name
          in der SL-Ansprache klingt: Wir gestalten die Zusammenarbeit so, dass sie authentisch zur Welt von
          Tales&amp;Legends passt – und zu deinen Zielen.
        </p>
      </Section>

      <Section title="Sponsoring-Pakete">
        <TierTable lang="de" />
      </Section>

      <Section title="Produkt- und Sachpartnerschaften">
        <p style={bodyStyle}>
          Nicht jede Partnerschaft ist Geld gegen Logo. Epic Armoury unterstützt uns zum Beispiel mit Rabatten auf
          Waffen und Ausrüstung — im Gegenzug zeigen wir ihre Produkte auf unseren Events und in unseren Kanälen.
          Wenn dein Unternehmen
          Material, Ausrüstung oder Dienstleistungen produziert, die zu LARP passen, sprich uns an — wir finden
          eine Form, die für beide Seiten funktioniert.
        </p>
      </Section>

      <Section title="Was Sponsoren bekommen – und was nicht">
        <p style={bodyStyle}>
          Was du bekommst: Sichtbarkeit in einer Nische mit hoher Kaufkraft für Handwerk, Outdoor, Gastronomie und
          Spiele, eine transparente Dokumentation der vereinbarten Leistungen und die Gewissheit, dass jeder Euro
          dokumentiert wird. Was du nicht bekommst: Einfluss auf Spielinhalte, Datenweitergabe von Teilnehmenden
          oder Werbung im Spiel selbst — die Taverne bleibt eine Taverne.
        </p>
      </Section>

      <Section title="Lass uns gemeinsam Geschichte schreiben">
        <p style={bodyStyle}>
          Du bist neugierig geworden? Dann freuen wir uns auf deine Nachricht. Wir erzählen dir gern mehr über
          unsere Events, unsere Community und die Möglichkeiten, die eine Partnerschaft mit Tales&amp;Legends dir
          bietet.
        </p>
        <p style={bodyStyle}>
          Schreib uns unter: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
          <br />
          Oder ruf einfach an: <a href="tel:+4935189672260">+49 351 8967226-0</a>
        </p>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
          Die Legende wartet. Werde Teil davon.
        </p>
      </Section>
    </div>
  );
}

/** Courtesy translation — German is the primary wording (see SponsoringDe). */
export function SponsoringEn() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
      <p style={bodyStyle}>
        Tales&amp;Legends is young: a tavern night with 20 guests, a fundraising campaign with 29 supporters, a team
        working toward our first weekend in our own game world, Realms of Lorell. Sponsoring now doesn't make
        you one logo among many — it makes you a founding partner of a community that's growing, and that
        demonstrably puts its money into culture and youth work.
      </p>
      <p style={bodyStyle}>
        Tales&amp;Legends creates fantasy live-action-roleplay events where stories aren't read, but lived. Our
        participants aren't passive spectators — they're protagonists. And you can become part of that story.
      </p>

      <Section title="Why Tales&Legends?">
        <p style={bodyStyle}>
          Tales&amp;Legends is organized as a non-profit UG (gUG) — because for us, LARP is more than a business
          model: it's cultural work. We believe that creative roleplay, collective storytelling, and living in other
          worlds have real social value — for imagination, empathy, and community.
        </p>
        <p style={bodyStyle}>
          For you as a sponsor, that means: your support flows directly into the quality of our events and into
          building a living community. No profit distributed to shareholders — just a team that pours its heart
          into every event.
        </p>
        <p style={bodyStyle}>
          Since sponsoring is a business transaction with something given in return, you can generally claim your
          contribution as a business expense. In return, we provide transparent documentation of the agreed
          services. Alternatively, you can make a donation instead and receive a donation receipt for it — take a
          look at our <Link to="/en/donate">donations page</Link> or contact us directly.
        </p>
      </Section>

      <Section title="What else sets us apart">
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Our own fantasy world</strong> — our events take place in a carefully built, original setting
            with history, factions, and living lore.
          </li>
          <li style={bodyStyle}>
            <strong>An active community</strong> — our participants keep coming back, recommend us to others, and
            live LARP all year round.
          </li>
          <li style={bodyStyle}>
            <strong>Real impact</strong> — you're not just supporting one event, but a cultural movement.
          </li>
        </ul>
      </Section>

      <Section title="What we offer you">
        <p style={bodyStyle}>
          Depending on the partnership, there are various ways to be present — from being named as an official
          sponsor, to having your own stand at the event marketplace, to further individual options. Whether your
          logo appears in the program, your product is sold at our events, or your name is mentioned in the game
          masters' announcements: we shape the collaboration so it fits authentically into the world of
          Tales&amp;Legends — and your goals.
        </p>
      </Section>

      <Section title="Sponsoring Packages">
        <TierTable lang="en" />
      </Section>

      <Section title="Product and In-Kind Partnerships">
        <p style={bodyStyle}>
          Not every partnership is money for a logo. Epic Armoury, for instance, supports us with discounts on
          weapons and gear — in return we showcase their products at our events and in our channels. If your
          company makes materials,
          equipment, or services that fit LARP, get in touch — we'll find a form that works for both sides.
        </p>
      </Section>

      <Section title="What Sponsors Get – and What They Don't">
        <p style={bodyStyle}>
          What you get: visibility in a niche with high purchasing power for craft, outdoor gear, hospitality, and
          games, transparent documentation of the agreed services, and the certainty that every euro is documented.
          What you don't get: influence over game content, participant data, or advertising inside the game itself
          — the tavern stays a tavern.
        </p>
      </Section>

      <Section title="Let's write history together">
        <p style={bodyStyle}>
          Curious? We'd love to hear from you. We're happy to tell you more about our events, our community, and
          the possibilities a partnership with Tales&amp;Legends offers.
        </p>
        <p style={bodyStyle}>
          Write to us at: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
          <br />
          Or just give us a call: <a href="tel:+4935189672260">+49 351 8967226-0</a>
        </p>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
          The legend is waiting. Become part of it.
        </p>
      </Section>
    </div>
  );
}
